/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type PropsWithChildren, useCallback, useRef, useState } from 'react';
import { parsePath } from 'react-router-dom';
import { DesktopRouter, type DesktopRouterLocation } from './DesktopRouter';
import { useShellNavigationContext } from './ShellNavigationContext';

/**
 * Router for chrome outside windows (toolbar, agent panel, toasts). It shows the focused
 * window location, and links inside it open the owning app window. Render it beside the window
 * layer, never around it.
 */
export function DesktopShellRouter({ children }: Readonly<PropsWithChildren>) {
  const { ambientRouter, go, location, navigate } = useShellNavigationContext();
  if (ambientRouter) {
    return children;
  }

  return (
    <DesktopRouter location={location} onGo={go} onNavigate={navigate}>
      {children}
    </DesktopRouter>
  );
}

/**
 * Router for desktop-level surfaces (widgets) with their own location at the desktop root.
 * Search-param state they write (grid filters, sort) stays local instead of taking over the
 * address bar; links to service pages open the owning app window.
 */
export function DesktopLocalRouter({ children }: Readonly<PropsWithChildren>) {
  const { ambientRouter, go, navigate } = useShellNavigationContext();
  const keyCounter = useRef(0);
  const [location, setLocation] = useState<DesktopRouterLocation>({ key: 'local', path: '/' });
  const handleNavigate = useCallback(
    (path: string, state: unknown, replace: boolean) => {
      if ((parsePath(path).pathname || '/') !== '/') {
        navigate(path, state, replace);
        return;
      }
      keyCounter.current += 1;
      setLocation({ key: `local-${keyCounter.current}`, path, state });
    },
    [navigate],
  );
  if (ambientRouter) {
    return children;
  }

  return (
    <DesktopRouter location={location} onGo={go} onNavigate={handleNavigate}>
      {children}
    </DesktopRouter>
  );
}
